<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('leave.assign_shift_permission') }}
      </oxd-text>

      <oxd-divider />

      <oxd-form
        :key="formInstanceKey"
        :loading="isLoading"
        @submit-valid="onSave"
      >
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="form.employee"
                :rules="rules.employee"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row v-if="form.employee?.id">
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.type"
                type="select"
                :label="$t('general.type')"
                :options="typeOptions"
                :rules="rules.type"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="form.leaveDate"
                :label="$t('general.date')"
                :rules="rules.leaveDate"
                :years="yearsArray"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row v-if="form.type?.id">
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <time-input
                v-model="form.expectedTime"
                :label="expectedTimeLabel"
                :rules="formRules.expectedTime"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row v-if="form.employee?.id">
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.comment"
                type="textarea"
                :label="$t('general.comments')"
                :rules="rules.comment"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <submit-button :label="$t('leave.assign')" />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {
  required,
  validSelection,
  validDateFormat,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';
import {yearRange} from '@ohrm/core/util/helper/year-range';
import {APIService} from '@ohrm/core/util/services/api.service';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {buildAttendanceReportReturnUrl} from '@/orangehrmTimePlugin/util/attendanceReportReturnUrl';
import {buildSubmitPayload} from '@/orangehrmLeavePlugin/util/shiftPermissionExpectedTime';
import {formatEmployeeFullName} from '@/core/util/helper/employeeDisplayName';

const TYPE_EARLY_LEAVE = 'early_leave';
const TYPE_LATE_ARRIVAL = 'late_arrival';

export default {
  name: 'AssignShiftPermission',

  components: {
    'employee-autocomplete': EmployeeAutocomplete,
  },

  props: {
    employee: {
      type: Object,
      default: null,
    },
    leaveDate: {
      type: String,
      default: null,
    },
    returnUrl: {
      type: String,
      default: null,
    },
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/employees/shift-permission-requests',
    );
    const {userDateFormat} = useDateFormat();

    return {
      http,
      userDateFormat,
    };
  },

  data() {
    return {
      isLoading: false,
      formInstanceKey: 0,
      yearsArray: [...yearRange()],
      form: {
        employee: null,
        type: null,
        leaveDate: null,
        expectedTime: '',
        comment: '',
      },
      typeOptions: [
        {
          id: TYPE_EARLY_LEAVE,
          label: this.$t('leave.early_leave'),
        },
        {
          id: TYPE_LATE_ARRIVAL,
          label: this.$t('leave.late_arrival'),
        },
      ],
      rules: {
        employee: [required, validSelection],
        type: [required, validSelection],
        leaveDate: [required, validDateFormat(this.userDateFormat)],
        comment: [required, shouldNotExceedCharLength(255)],
      },
    };
  },

  computed: {
    isLateArrival() {
      return this.form.type?.id === TYPE_LATE_ARRIVAL;
    },
    isEarlyLeave() {
      return this.form.type?.id === TYPE_EARLY_LEAVE;
    },
    expectedTimeLabel() {
      if (this.isEarlyLeave) {
        return this.$t('leave.expected_departure_time');
      }
      if (this.isLateArrival) {
        return this.$t('leave.expected_arrival_time');
      }
      return this.$t('leave.expected_arrival_or_departure');
    },
    formRules() {
      return {
        ...this.rules,
        expectedTime: this.form.type?.id ? [required] : [],
      };
    },
  },

  watch: {
    'form.type'() {
      this.form.expectedTime = '';
    },
  },

  created() {
    this.applyPrefillFromProps();
  },

  methods: {
    resolveReturnUrl() {
      if (!this.returnUrl) {
        return null;
      }
      if (
        this.returnUrl.includes('startDate=') ||
        this.returnUrl.includes('fromDate=')
      ) {
        return this.returnUrl;
      }
      const params = new URLSearchParams(window.location.search);
      const reportStart =
        params.get('reportStartDate') || params.get('startDate');
      const reportEnd = params.get('reportEndDate') || params.get('endDate');
      return buildAttendanceReportReturnUrl(reportStart, reportEnd);
    },
    applyPrefillFromProps() {
      if (this.employee?.empNumber != null) {
        this.form.employee = {
          id: this.employee.empNumber,
          label: formatEmployeeFullName(this.employee),
          isPastEmployee: Boolean(this.employee.terminationId),
        };
      }

      const params = new URLSearchParams(window.location.search);
      const empNumberParam = params.get('empNumber');
      if (!this.form.employee?.id && empNumberParam) {
        this.http
          .request({
            method: 'GET',
            url: `/api/v2/pim/employees/${empNumberParam}`,
          })
          .then((response) => {
            const employee = response.data?.data;
            if (!employee) {
              return;
            }
            this.form.employee = {
              id: employee.empNumber,
              label: formatEmployeeFullName(employee),
              isPastEmployee: Boolean(employee.terminationId),
            };
          })
          .catch(() => undefined);
      }

      const leaveDateParam = this.leaveDate ?? params.get('leaveDate');
      if (leaveDateParam) {
        const parsed = parseDate(leaveDateParam, 'yyyy-MM-dd');
        if (parsed) {
          this.form.leaveDate = formatDate(parsed, 'yyyy-MM-dd');
        }
      }
    },
    buildPayload() {
      return {
        empNumber: this.form.employee.id,
        type: this.form.type.id,
        ...buildSubmitPayload({
          leaveDate: this.form.leaveDate,
          expectedTime: this.form.expectedTime,
          comment: this.form.comment,
        }),
      };
    },
    createEmptyForm() {
      return {
        employee: null,
        type: null,
        leaveDate: null,
        expectedTime: '',
        comment: '',
      };
    },
    onSave() {
      this.isLoading = true;
      this.http
        .create(this.buildPayload())
        .then(() => {
          this.$toast.saveSuccess();
          const returnUrl = this.resolveReturnUrl();
          if (returnUrl) {
            navigate(returnUrl);
            return;
          }
          this.form = this.createEmptyForm();
          this.formInstanceKey += 1;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
